import React from 'react'
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';

// Components
import CommandBox, { COMMAND_BOX_FLAG } from './Components/CommandBox';
import HeroStatsDisplay from './Components/HeroStatsDisplay';
import Map from './Components/Map';
import FightDisplayBox from './Components/FightDisplayBox';
import CityDisplayBox from './Components/CityDisplayBox';
import InventoryBox from './Components/InventoryBox';
import LogsBox from './Components/LogsBox';
import ShopBox from './Components/ShopBox';
import EventBox from './Components/EventBox';
// Dependencies
import Modal from './Dependencies/Modal';
// Store
import { heroActions } from './Store/Actions/heroActions';
import { gameDataActions } from './Store/Actions/gameDataActions';
import { getGameState } from './Store/Selectors/gameDataSelectors';
import { getGameData, getHero, getMonsters, getUser } from './Store/Selectors/selectors';
// Helpers
import { GAME_STATE, MODAL_FLAG } from "./Helpers/constants";
import { log } from "./Helpers/logger";

const INPUT_KEYS = {
    Z: 90,
    Q: 81,
    S: 83,
    D: 68,
    I: 73
}

class Core extends React.Component {
    refSprite = React.createRef();

    oldKeysDown = {};
    keysDown = {};

    shouldComponentUpdate() {
        // log.ui.log('Core::shouldComponentUpdate')
        return true
    }

    render() {
        log.ui.log('Core::render - hero:', this.props.hero);
        log.ui.log('Core::render - hero position:', this.props.hero?.position);
        log.ui.log('Core::render - gameData:', this.props.gameData)
        const { callback, dispatch, gameData: { gameState, commandBoxFlag, fightData: { endFightData }, displayModal, cityData }, hero } = this.props;

        // (body.style is mutated in componentDidUpdate, NOT here — touching
        // document during render breaks React 18's concurrent reconciliation
        // and produced the "removeChild: not a child of this node" crash on
        // the ONGOING → FIGHT transition.)

        const { t } = this.props;
        const heroName = hero?.name || t('heroStats.fallbackName');

        return (
            <div className="okframe okframe-page" data-screen-label="03 Game" style={{ padding: '32px 32px 48px' }}>
                <div className="okframe-brand">
                    <img src="/assets/okoyl-logo.png" alt="Okoyl" />
                </div>
                <div className="okframe-head" style={{ marginBottom: 24 }}>
                    <div className="okframe-title">{heroName}</div>
                    <div className="okframe-sub">{t('game.subtitle')}</div>
                    <div className="okframe-rule" />
                </div>
                <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* HUD pleine largeur — portrait + barres + attributs */}
                    <HeroStatsDisplay />

                    {/* Map à gauche, commandes à droite */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
                        {/* Colonne gauche — main content (map / fight / victory / gameover + cityDisplay) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {(gameState === GAME_STATE.ONGOING || gameState === GAME_STATE.MODAL) && (
                                <div className="okcard okcard-bare" style={{ padding: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <span className="oklbl oklbl-gold">{t('game.regionLabel', { name: t('game.regionFallback') })}</span>
                                        <span className="oklbl oklbl-muted">{t('game.coordLabel', { x: hero?.position?.x ?? 1, y: hero?.position?.y ?? 1 })}</span>
                                    </div>
                                    <Map
                                        mapId="map1"
                                        iconPosition={{
                                            x: hero?.position?.x || 1,
                                            y: hero?.position?.y || 1,
                                        }}
                                        elementStyle={{ height: '64px', width: '64px' }}
                                    />
                                </div>
                            )}
                            {gameState === GAME_STATE.FIGHT && <FightDisplayBox />}
                            {gameState === GAME_STATE.VICTORY && (
                                <div className="okcard" style={{ textAlign: 'center', minHeight: 300 }}>
                                    <div className="okframe-title" style={{ fontSize: 28 }}>{t('victory.title')}</div>
                                    <div className="okframe-rule" />
                                    <p className="oklore" style={{ marginTop: 16 }}>{t('victory.lore')}</p>
                                    <p style={{ marginTop: 12, fontFamily: 'var(--font-mono)', color: 'var(--okoyl-200)' }}>
                                        {t('victory.rewards', { xp: endFightData.expDrop, gold: endFightData.goldDrop })}
                                    </p>
                                    <button type="button" className="okbtn okbtn-primary" style={{ marginTop: 18 }} onClick={() => dispatch(gameDataActions.setToNormalMode())}>
                                        {t('victory.resume')}
                                    </button>
                                </div>
                            )}
                            {gameState === GAME_STATE.GAME_OVER && (
                                <div className="okcard" style={{ textAlign: 'center', minHeight: 300, padding: 48 }}>
                                    <div className="okframe-title" style={{ fontSize: 32, color: 'var(--crit)' }}>{t('gameOver.title')}</div>
                                    <div className="okframe-rule" />
                                    <p className="oklore" style={{ marginTop: 24 }}>{t('gameOver.lore')}</p>
                                </div>
                            )}
                            <CityDisplayBox cityData={cityData} />
                        </div>

                        {/* Colonne droite — boîte de commandes (movement / fight / shop options) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <CommandBox flag={commandBoxFlag} />
                        </div>
                    </div>

                    {/* Journal pleine largeur en bas */}
                    <LogsBox />

                    {/* Debug panel — only in dev, full width */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className="okcard" style={{ opacity: 0.85 }}>
                            <span className="oklbl oklbl-muted">{t('dev.panelLabel')}</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(heroActions.gainXp(100))}>{t('dev.buttons.gainXp')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(heroActions.takeDamage(10))}>{t('dev.buttons.takeDamage')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(gameDataActions.setGameState(GAME_STATE.GAME_OVER))}>{t('dev.buttons.gameOver')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(gameDataActions.setGameState(GAME_STATE.ONGOING))}>{t('dev.buttons.ongoing')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(gameDataActions.setGameState(GAME_STATE.FIGHT))}>{t('dev.buttons.fight')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(gameDataActions.setCommandBoxFlag(COMMAND_BOX_FLAG.FIGHT))}>{t('dev.buttons.fightMode')}</button>
                                <button type="button" className="okbtn okbtn-secondary okbtn-sm" onClick={() => dispatch(gameDataActions.setCommandBoxFlag(COMMAND_BOX_FLAG.MOVE))}>{t('dev.buttons.moveMode')}</button>
                            </div>
                        </div>
                    )}
                </div>

                {displayModal.show && displayModal.flag === MODAL_FLAG.INVENTORY && (
                    <Modal onLeave={this.leaveModal}><InventoryBox onClose={this.leaveModal} /></Modal>
                )}
                {displayModal.show && displayModal.flag === MODAL_FLAG.SHOP && (
                    <Modal onLeave={this.leaveModal}><ShopBox currentShop={displayModal.data} /></Modal>
                )}
                {displayModal.show && displayModal.flag === MODAL_FLAG.EVENT && (
                    <Modal onLeave={this.leaveModal}><EventBox /></Modal>
                )}
            </div>
        );
    }

    componentDidMount() {
        const { dispatch, hero, user } = this.props;
        
        // Add event listeners for keyboard input
        document.addEventListener('keydown', this._keyDownHandler);
        document.addEventListener('keyup', this._keyUpHandler);
        
        // Always try to initialize hero from user game data if available
        // This ensures we have the latest hero data when the component mounts
        if (user?.gameData?.hero) {
            log.ui.log('Initializing hero from user game data');
            // Use the heroActions.setHero action to properly set the hero
            dispatch({
                type: 'hero set hero',
                payload: { hero: user.gameData.hero }
            });
        } else if (hero) {
            log.ui.log('Hero data already loaded:', hero);
        } else {
            log.ui.log('No hero data available in user.gameData');
        }
    }

    componentWillUnmount(){
        document.removeEventListener('keydown', this._keyDownHandler);
        document.removeEventListener('keyup', this._keyUpHandler);
        // Reset body scroll if Core unmounts while a modal was open.
        document.body.style.overflowY = 'auto';
    }

    componentDidUpdate(prevProps) {
        const {
            callback,
            gameData: { gameState, displayModal },
            hero,
            user
        } = this.props;

        // Lock body scroll while a modal is up — moved out of render() so
        // it never runs during reconciliation.
        const showModal = !!displayModal?.show;
        const wasShowing = !!prevProps.gameData?.displayModal?.show;
        if (showModal !== wasShowing) {
            document.body.style.overflowY = showModal ? 'hidden' : 'auto';
        }

        // Check for game over state
        if (callback && gameState === GAME_STATE.GAME_OVER) {
            callback(false);
        }

        // Only re-dispatch when the hero object inside user.gameData
        // actually changed (deep ref). Comparing on `user !== prevProps.user`
        // alone fired this every render once user got spread elsewhere,
        // which during the ONGOING → FIGHT transition caused an extra
        // dispatch mid-commit and was a candidate for the React 18
        // removeChild crash.
        const prevHero = prevProps.user?.gameData?.hero;
        const nextHero = user?.gameData?.hero;
        if (nextHero && nextHero !== prevHero) {
            this.props.dispatch(heroActions.setHero(nextHero));
        }
    }

    leaveModal = () => {
        const { dispatch } = this.props;
        dispatch(gameDataActions.modalData.switch());
        dispatch(gameDataActions.switchBackToOldGameState());
    }

    _keyDownHandler = (event) => {
        log.ui.log('_keyDownHandler', event)
        const { keyCode } = event;
        

        this.oldKeysDown = { ...this.keysDown };
        this.keysDown[keyCode] = true;

        log.ui.log(this.keysDown)

        this._checkInputsDown(keyCode)
    }

    _keyUpHandler = (event) => {
        log.ui.log('_keyUpHandler', event)
        const { keyCode } = event;
        // const { dispatch } = this.props;
        this.oldKeysDown = { ...this.keysDown }
        this.keysDown[keyCode] = false;

        log.ui.log(this.keysDown)

        this._checkInputsDown(keyCode)

        // if (keyCode === 83 || keyCode === 40) {
        //     log.ui.log("On va vers le bas");
        //     dispatch(heroActions.move(0, 1))
        // }
    }

    _checkInputsDown(keyCode) {
        const { dispatch, gameData } = this.props;
        
        if (getGameState(gameData) === GAME_STATE.ONGOING) {
            if (this.keysDown[keyCode] && !this.oldKeysDown[keyCode]) {
                switch (keyCode) {
                    case INPUT_KEYS.Z: {
        
                        log.ui.log("On va vers le haut");
                        dispatch(heroActions.move(0, -1));
        
                        break;
                    }
                    case INPUT_KEYS.Q: {
        
                        log.ui.log("On va vers la gauche");
                        dispatch(heroActions.move(-1, 0));
        
                        break;
                    }
                    case INPUT_KEYS.S: {
        
                        log.ui.log("On va vers le bas");
                        dispatch(heroActions.move(0, 1));
        
                        break;
                    }
                    case INPUT_KEYS.D: {
        
                        log.ui.log("On va vers la droite");
                        dispatch(heroActions.move(1, 0));
        
                        break;
                    }
                    case INPUT_KEYS.I: {
        
                        log.ui.log("On ouvre l'inventaire");
                        dispatch(gameDataActions.modalData.set(true, MODAL_FLAG.INVENTORY));
                        dispatch(gameDataActions.setGameState(GAME_STATE.MODAL));
        
                        break;
                    }
                    default:
                        break;
                }
            }
        }
        
    }
    // setPlayerIsALive(){
    //     return commandBoxFlag;
    // }
    // callback()
}

const styles = {
    sprite: (props) => {
        const { texture, height, width, offSetX, offSetY } = props;

        return {
            background: 'url(' + texture + ') ' + offSetX + 'px ' + offSetY + 'px no-repeat',
            height,
            width
        }
    },
}

const mapStateToProps = (state) => {
    return {
        hero: getHero(state),
        monsters: getMonsters(state),
        gameData: getGameData(state),
        user: getUser(state)
    };
}

export default withTranslation()(connect(mapStateToProps)(Core));